import { useEffect, useState } from "react"
import { useCommonDispatch, useCommonState } from "../stores/common"
import HeaderBar from "../components/HeaderBar"
import FullScreenLoading from '../components/FullScreenLoading'
import { Link, useLocation, useNavigate } from "react-router-dom"
import { getMe } from '../apis/auth'
import { updateAbility } from '../ability'
import { hasPermissions } from "../utils"
import NoPermission from "../pages/NoPermission"

/**
 * Map role string to legacy ModuleFeatures format
 * so existing components continue to work
 */
const mapRoleToModuleFeatures = (role) => {
    switch (role) {
        case 'Requestor': return ['View', 'Submit', 'Edit']
        case 'Approver': return ['View', 'Approve']
        case 'Admin': return ['View', 'Submit', 'Edit', 'Admin']
        case 'Viewer': return ['View']
        default: return []
    }
}

const MainLayout = (props) => {

    const navigate = useNavigate()

    const { allowedPerms } = props

    const commonStore = useCommonState()
    const dispatch = useCommonDispatch()
    const [breadcrumbText, setBreadcrumbText] = useState('')
    const location = useLocation()

    const isAddNewUserPage = location.pathname.includes('add-new-user');
    const isManageUsersPage = location.pathname.includes('manage-users');
    const isSearchCase = location.pathname.includes('search');

    const [canGoBack, setCanGoBack] = useState(false)
    const [perms, setPerms] = useState(false)
    const [allowed, setAllowed] = useState()
    const [loadingPermissions, setLoadingPermissions] = useState(true)

    useEffect(() => {
        // eslint-disable-next-line react-hooks/exhaustive-deps
        let routers = location.pathname.replace('/dashboard/', '')
        routers = routers.split('/')
        if ((typeof routers[1] !== 'undefined' && routers[1] !== '') || routers[0] === 'manage-users'|| routers[0] === 'search') {
            setCanGoBack(true)
        }
        /**
         * Get permissions via /auth/me (RS-006 dual-source)
         * - abilities: CASL gate check (ViewNCCRC)
         * - role: from local users table (Requestor/Approver/Viewer/Admin)
         */
        const _getPermissions = async () => {
            setLoadingPermissions(true)
            const meData = await getMe()
            if (meData.success && meData.role) {
                // Abilities may be empty — ViewNCCRC is only granted to select users for
                // Manage Users access. Base permissions come from role.
                const abilities = meData.abilities || []
                updateAbility(abilities)
                dispatch({ type: 'NCC_ABILITIES', payload: abilities })

                const role = meData.role
                dispatch({ type: 'CURRENT_ROLE', payload: role })

                // Map role to legacy ModuleFeatures for backward compatibility
                const moduleFeatures = mapRoleToModuleFeatures(role)
                dispatch({ type: 'PERMISSIONS', payload: moduleFeatures })
                setPerms(moduleFeatures)
            }
            else {
                if (process.env.REACT_APP_FAKE_PERMS) {
                    try {
                        const fakePerms = JSON.parse(process.env.REACT_APP_FAKE_PERMS)
                        dispatch({ type: 'PERMISSIONS', payload: fakePerms })
                        setPerms(fakePerms)
                    }
                    catch (error) {
                        console.log('=JSON-parse-ERROR=', error)
                        setPerms([])
                    }
                }
                else {
                    setPerms([])
                }
            }
            setLoadingPermissions(false)
        }
        _getPermissions()

        if (isAddNewUserPage) {
            setBreadcrumbText('Add New User')
        }
        else if (isManageUsersPage) {
            setBreadcrumbText('Manage Users')
        }
        else if ( isSearchCase ) {
            setBreadcrumbText('Search')
        }
        else {
            setBreadcrumbText('')
        }

    }, [])

    useEffect(() => {
        if ( commonStore.currentRole === 'Viewer' ) {
            let routers = location.pathname.replace('/dashboard/', '')
            routers = routers.split('/')
            if ( routers[0] !== 'search' ) {
                navigate( '/dashboard/search' )
            }
        }
    }, [commonStore.currentRole])

    useEffect(() => {

        if (perms === false) {
            return
        }

        if (Array.isArray(perms) && perms.length > 0 && hasPermissions(perms, allowedPerms)) {
            setAllowed(true)
        }
        else {
            setAllowed(false)
        }
    }, [perms])

    return (
        <div className="ncc-pos-relative">
            {commonStore.isInitializing ?
                <FullScreenLoading />
                :
                <>
                    <HeaderBar canGoBack={canGoBack} />

                    {loadingPermissions ?
                        <FullScreenLoading inside={true} />
                        :
                        <>

                            {canGoBack && allowed &&
                                <div className="container-lg mt-3 mb-3">
                                    <nav aria-label="breadcrumb">
                                        <ol className="breadcrumb">
                                            <li className="breadcrumb-item" aria-current="page">
                                                <Link to={'/dashboard/cases'}>Home</Link>
                                            </li>
                                            <li className="breadcrumb-item active" aria-current="page">{breadcrumbText !== '' ? breadcrumbText : 'Case Details'}</li>
                                        </ol>
                                    </nav>
                                </div>}

                            <div className="container-lg">
                                {allowed && props.children}

                                {allowed === false &&
                                    <h1 className="mt-5 mb-5 font-weight-bold text-center">
                                        <NoPermission/>
                                    </h1>}
                            </div>
                        </>}
                </>
            }
        </div>
    )
}

MainLayout.defaultProps = {
    allowedPerms: []
}

export default MainLayout
